// =============================================================================
// clientes/src/lib/consultas.ts — o que o portal pergunta ao banco.
//
// Curto de propósito: o parceiro só enxerga o que as RPCs do portal devolvem
// (sql/functions/portal_b2b.sql) e os PRÓPRIOS pedidos (rls.sql §17). Tudo o
// mais é default-deny — este arquivo não tem como crescer para o lado errado.
// =============================================================================
import type {
  CatalogoB2BRow, FormaPagamentoB2B, PartnerOrder, PartnerOrderItem,
} from '@core/types';
import { supabase } from './supabase';

function ou<T>({ data, error }: { data: T | null; error: { message: string } | null }): T | null {
  if (error) throw new Error(error.message);
  return data;
}

export interface MeuCadastro {
  nome: string;
  documento: string | null;
  desconto_pct: number;
}

/** Quem sou eu no portal — null = login sem chapéu de parceiro. */
export async function meuCadastro(): Promise<MeuCadastro | null> {
  const linhas = ou(await supabase.rpc('meu_cadastro_b2b')) as MeuCadastro[] | null;
  return linhas?.[0] ?? null;
}

export async function catalogo(
  q: string, categoria: string | null, limite = 24, offset = 0,
): Promise<CatalogoB2BRow[]> {
  return ou(await supabase.rpc('catalogo_b2b', {
    q: q.trim() || null, p_categoria: categoria, p_limite: limite, p_offset: offset,
  })) as CatalogoB2BRow[] ?? [];
}

export async function categorias(): Promise<Array<{ categoria: string; itens: number }>> {
  return ou(await supabase.rpc('categorias_b2b')) as Array<{ categoria: string; itens: number }> ?? [];
}

export async function criarPedido(p: {
  itens: Array<{ product_id: string; quantidade: number }>;
  forma: FormaPagamentoB2B;
  observacao?: string;
  idempotencyKey: string;
}): Promise<string> {
  const id = ou(await supabase.rpc('criar_pedido_b2b', {
    p_itens: p.itens,
    p_forma: p.forma,
    p_observacao: p.observacao ?? null,
    p_idempotency_key: p.idempotencyKey,
  })) as string | null;
  if (!id) throw new Error('O pedido não foi criado — tente de novo.');
  return id;
}

export async function cancelarPedido(id: string, motivo?: string): Promise<void> {
  ou(await supabase.rpc('cancelar_pedido_b2b', { p_id: id, p_motivo: motivo ?? null }));
}

export interface MeuPedido extends PartnerOrder {
  partner_order_items: PartnerOrderItem[];
}

/** A RLS entrega SÓ os meus (customer_id = parceiro_atual()). */
export async function meusPedidos(): Promise<MeuPedido[]> {
  return ou(await supabase
    .from('partner_orders')
    .select('*, partner_order_items(*)')
    .order('created_at', { ascending: false })
    .limit(100)) as MeuPedido[] ?? [];
}
